// What the Ask dock says it is looking at. Pure: derived from the URL and the
// store's own channel record, never from page text. The server re-verifies
// every claim made here (ownership, visibility) before acting on it.
import type { ChannelViewType } from '../types';

export interface AskChannelFacts {
  title: string;
  isPublic: boolean;
  owner: boolean;
  viewType?: ChannelViewType;
}

export interface AskContext {
  route: string;
  /** Nickname of the channel the page is about, if any. */
  channel?: string;
  /** messages | charts | reports | details | settings | sharing | ... */
  tab?: string;
  reportNumber?: string;
  /** Short place name, e.g. "My Channels". */
  area: string;
  /** Second chip segment, e.g. "Reports". */
  tabLabel?: string;
  /** "Public" | "Private" | "Read-only" parts shown in the detail popover. */
  visibility?: 'Public' | 'Private';
  readOnly?: boolean;
}

const TAB_LABEL: Record<string, string> = {
  messages: 'Signals',
  charts: 'Charts',
  reports: 'Reports',
  posts: 'Reports',
  details: 'Details',
  edit: 'Settings',
  share: 'Sharing',
  insights: 'Insights',
  'ai-settings': 'AI settings',
  workflows: 'Workflows',
  posts_new: 'New post',
};

const SUBPAGE: Record<string, string> = {
  details: 'details',
  edit: 'edit',
  share: 'share',
  insights: 'insights',
  'ai-settings': 'ai-settings',
  workflows: 'workflows',
  posts: 'posts_new',
};

const AREAS: [RegExp, string][] = [
  [/^\/channels(\/(new|pick|invites))?\/?$/, 'My Channels'],
  [/^\/showcase/, 'Explore'],
  [/^\/following/, 'Following'],
  [/^\/templates/, 'Schemas'],
  [/^\/integrations/, 'Sources'],
  [/^\/mappings/, 'Sources'],
  [/^\/settings/, 'Settings'],
  [/^\/diagnostics/, 'Diagnostics'],
  [/^\/public\/?$/, 'Explore'],
];

/** Routes where the dock must not appear at all (auth, legal, marketing-like). */
export function dockHiddenOn(pathname: string): boolean {
  return /^\/(start|login|register|reset-password|oauth|terms|privacy|cookies|subprocessors|dpa|legal|status|ask|shared)(\/|$)/.test(pathname);
}

/** A same-origin path safe to navigate back to ("/x", never "//host" or a scheme). */
export function safeReturnPath(p: string | null | undefined): string | null {
  return p && /^\/(?![/\\])/.test(p) ? p : null;
}

/** True for routes rendered inside the signed-in app shell (sidebar + footer). */
export function inAppShell(pathname: string): boolean {
  return !pathname.startsWith('/public');
}

export function deriveAskContext(pathname: string, channel?: AskChannelFacts): AskContext {
  const base: AskContext = { route: pathname, area: 'Zmist' };

  let m = pathname.match(/^\/channels\/([^/]+)(?:\/([^/]+))?/);
  if (m && !['new', 'pick', 'invites'].includes(m[1])) {
    const sub = m[2] ? SUBPAGE[m[2]] ?? m[2] : undefined;
    const tab = sub ?? (channel?.viewType === 'posts' ? 'reports' : channel?.viewType) ?? 'messages';
    return withChannel({ ...base, channel: decodeURIComponent(m[1]), tab, area: channel?.title || decodeURIComponent(m[1]), tabLabel: TAB_LABEL[tab] ?? tab }, channel);
  }
  m = pathname.match(/^\/public\/([^/]+)\/reports\/([^/]+)/);
  if (m) {
    return withChannel({ ...base, channel: decodeURIComponent(m[1]), tab: 'reports', reportNumber: m[2], area: channel?.title || decodeURIComponent(m[1]), tabLabel: `Report #${m[2]}` }, channel);
  }
  m = pathname.match(/^\/public\/([^/]+)/);
  if (m) {
    return withChannel({ ...base, channel: decodeURIComponent(m[1]), tab: 'public', area: channel?.title || decodeURIComponent(m[1]), tabLabel: 'Public channel' }, channel);
  }
  for (const [re, area] of AREAS) if (re.test(pathname)) return { ...base, area };
  return base;
}

function withChannel(c: AskContext, facts?: AskChannelFacts): AskContext {
  if (!facts) return c;
  return { ...c, visibility: facts.isPublic ? 'Public' : 'Private', readOnly: !facts.owner };
}

/** The text of the chip, e.g. "Apple vs Microsoft · Reports". */
export function chipText(c: AskContext): string {
  return c.tabLabel ? `${c.area} · ${c.tabLabel}` : c.area;
}

/** The context object sent with a message (the server re-verifies it). */
export function toWire(c: AskContext, pinned?: string) {
  return {
    route: c.route,
    ...(c.channel ? { channel: c.channel } : {}),
    ...(c.tab ? { tab: c.tab } : {}),
    ...(c.reportNumber ? { reportNumber: c.reportNumber } : {}),
    ...(pinned ? { pinned } : {}),
  };
}
